import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import {
    LayoutDashboard, KanbanSquare, Users, Settings, Bell, Search, LogOut,
    Sparkles, Bot, FolderTree, Ticket, BarChart3, HelpCircle, ChevronDown, Plus, Globe
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import BrandMark from "@/components/BrandMark";
import ModuleAccent from "@/components/ModuleAccent";

function NavItem({ to, icon: Icon, label, badge, testid }) {
    return (
        <NavLink
            to={to}
            data-testid={testid}
            className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`}
        >
            <Icon size={16} />
            <span>{label}</span>
            {badge != null && badge > 0 && (
                <span
                    className="ml-auto text-[10px] font-bold px-1.5 py-0.5 rounded-full text-white"
                    style={{ background: "var(--mod-accent)" }}
                >
                    {badge}
                </span>
            )}
        </NavLink>
    );
}

export default function AppLayout() {
    const { user, logout } = useAuth();
    const nav = useNavigate();
    const location = useLocation();
    const [notifications, setNotifications] = useState([]);
    const [unread, setUnread] = useState(0);
    const [openBell, setOpenBell] = useState(false);
    const [openUser, setOpenUser] = useState(false);
    const [jobCount, setJobCount] = useState(0);

    const loadNotifications = async () => {
        try {
            const { data } = await api.get("/notifications");
            setNotifications(data);
            setUnread(data.filter((n) => !n.read).length);
        } catch {}
    };

    useEffect(() => {
        if (!user || user === false) return;
        loadNotifications();
        (async () => {
            try {
                const { data } = await api.get("/jobs");
                setJobCount(data.length);
            } catch {}
        })();
    }, [user, location.pathname]);

    const markAllRead = async () => {
        await api.post("/notifications/read-all");
        loadNotifications();
    };

    const handleLogout = async () => {
        await logout();
        nav("/login");
    };

    const tenantInitials = user?.tenant?.name
        ?.split(" ")
        .map((w) => w[0])
        .slice(0, 2)
        .join("")
        .toUpperCase() || "??";

    const breadcrumbMap = {
        "/dashboard": ["Dashboard", "Operativa"],
        "/commesse": ["Lavoro", "Commesse"],
        "/clienti": ["Lavoro", "Clienti"],
        "/inbox": ["Automazione", "Inbox AI"],
        "/telegram": ["Automazione", "Bot Telegram"],
        "/landing": ["Sistema", "Landing"],
        "/configurazione": ["Sistema", "Configurazione"],
        "/superadmin": ["Piattaforma", "Superadmin"],
    };
    const crumb = breadcrumbMap[location.pathname] || ["Dashboard", "Home"];
    const moduleId = user?.role === "superadmin" ? "superadmin" : "totalpl";

    return (
        <ModuleAccent module={moduleId} backdrop={false} className="min-h-screen grid" style={{ gridTemplateColumns: "240px 1fr" }}>
            {/* SIDEBAR */}
            <aside
                className="h-screen sticky top-0 flex flex-col gap-5 p-4"
                style={{
                    background: "#05050F",
                    color: "#fff",
                    borderRight: "1px solid rgba(255,255,255,.06)",
                    fontFamily: "Geist, Outfit, sans-serif",
                    position: "relative",
                }}
            >
                {/* subtle mod-accent glow */}
                <div
                    aria-hidden
                    style={{
                        position: "absolute",
                        top: "-30%",
                        left: "-20%",
                        width: "240px",
                        height: "240px",
                        background: "radial-gradient(circle, var(--mod-accent-glow) 0%, transparent 60%)",
                        filter: "blur(40px)",
                        opacity: 0.65,
                        pointerEvents: "none",
                    }}
                />
                <div className="pb-4 border-b border-white/10">
                    <BrandMark size={42} theme="dark" to={user?.role === "superadmin" ? "/superadmin" : "/dashboard"} />
                </div>

                {user?.role === "superadmin" ? (
                    <nav className="flex flex-col gap-0.5">
                        <div className="text-[9px] mono font-semibold uppercase tracking-[0.18em] text-white/50 px-3 py-1.5">// Piattaforma</div>
                        <NavItem to="/superadmin" icon={LayoutDashboard} label="Tenants" testid="nav-superadmin" />
                    </nav>
                ) : (
                    <>
                        <nav className="flex flex-col gap-0.5">
                            <div className="text-[9px] mono font-semibold uppercase tracking-[0.18em] text-white/50 px-3 py-1.5">// Operativa</div>
                            <NavItem to="/dashboard" icon={LayoutDashboard} label="Dashboard" testid="nav-dashboard" />
                            <NavItem to="/commesse" icon={KanbanSquare} label="Commesse" badge={jobCount} testid="nav-commesse" />
                            <NavItem to="/clienti" icon={Users} label="Clienti" testid="nav-clienti" />
                        </nav>

                        <nav className="flex flex-col gap-0.5">
                            <div className="text-[9px] mono font-semibold uppercase tracking-[0.18em] text-white/50 px-3 py-1.5">// Automazione</div>
                            <NavItem to="/telegram" icon={Bot} label="Bot Telegram" testid="nav-telegram" />
                            <NavItem to="/inbox" icon={Sparkles} label="Inbox AI" testid="nav-inbox" />
                        </nav>

                        {user?.role === "admin" && (
                            <nav className="flex flex-col gap-0.5">
                                <div className="text-[9px] mono font-semibold uppercase tracking-[0.18em] text-white/50 px-3 py-1.5">// Sistema</div>
                                <NavItem to="/landing" icon={Globe} label="Landing" testid="nav-landing" />
                                <NavItem to="/configurazione" icon={Settings} label="Configurazione" testid="nav-config" />
                            </nav>
                        )}

                        <nav className="flex flex-col gap-0.5">
                            <div className="text-[9px] mono font-semibold uppercase tracking-[0.18em] text-white/50 px-3 py-1.5">// Risorse</div>
                            <NavItem to="/manuale/tipografia" icon={HelpCircle} label="Guida operatore" testid="nav-manual" />
                        </nav>
                    </>
                )}

                <div className="mt-auto relative">
                    {user?.tenant && (
                        <div className="p-2.5 rounded-xl flex items-center gap-2.5"
                            style={{ background: "rgba(255,255,255,.04)", border: "1px solid rgba(255,255,255,.08)", backdropFilter: "blur(8px)" }}>
                            <div className="w-8 h-8 rounded-lg grid place-items-center text-white font-bold text-[10px]"
                                style={{ background: "linear-gradient(135deg, var(--mod-accent), var(--mod-accent-deep))", boxShadow: "0 8px 20px -6px var(--mod-accent-glow)" }}>
                                {tenantInitials}
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="text-[12px] font-semibold truncate" style={{ fontFamily: "Geist, sans-serif" }}>{user.tenant.name}</div>
                                <div className="text-[9px] uppercase tracking-[0.18em] text-white/45 mt-0.5" style={{ fontFamily: "JetBrains Mono, monospace" }}>PLAN · {user.tenant.plan?.toUpperCase()}</div>
                            </div>
                        </div>
                    )}
                </div>
            </aside>

            {/* MAIN */}
            <div className="flex flex-col min-h-screen relative" style={{ background: "var(--bg)" }}>
                {/* Soft module-color radial signature top-right */}
                <div
                    aria-hidden
                    className="pointer-events-none absolute"
                    style={{
                        top: -160,
                        right: -160,
                        width: 520,
                        height: 520,
                        borderRadius: "50%",
                        background: "radial-gradient(circle, var(--mod-accent-glow) 0%, transparent 65%)",
                        filter: "blur(40px)",
                        opacity: 0.55,
                        zIndex: 0,
                    }}
                />
                <header className="flex items-center justify-between px-8 py-4 sticky top-0 z-30 relative"
                    style={{ background: "var(--bg)", borderBottom: "1px solid var(--line)" }}>
                    <div className="mono uppercase tracking-[0.1em] text-xs font-semibold" style={{ color: "var(--muted-2)" }}>
                        {crumb[0]} / <span style={{ color: "var(--navy)" }}>{crumb[1]}</span>
                    </div>
                    <div className="flex items-center gap-2 relative">
                        <button
                            data-testid="topbar-notifications"
                            onClick={() => { setOpenBell((v) => !v); setOpenUser(false); }}
                            className="w-9 h-9 rounded-lg grid place-items-center relative bg-white hover:border-pink-300 transition-colors"
                            style={{ border: "1px solid var(--line)", color: "var(--navy)" }}
                        >
                            <Bell size={16} />
                            {unread > 0 && (
                                <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full text-[9px] font-bold text-white grid place-items-center"
                                    style={{ background: "var(--magenta)", border: "2px solid var(--bg)" }}>
                                    {unread}
                                </span>
                            )}
                        </button>
                        {openBell && (
                            <div className="absolute right-0 top-12 w-96 card-tpl shadow-xl overflow-hidden" style={{ borderColor: "var(--line-2)" }}>
                                <div className="p-3 flex justify-between items-center" style={{ borderBottom: "1px solid var(--line)" }}>
                                    <span className="font-bold text-sm" style={{ color: "var(--navy)" }}>Notifiche</span>
                                    <button onClick={markAllRead} className="mono text-[10px] uppercase tracking-wider hover:underline" style={{ color: "var(--cobalt)" }}>
                                        Segna tutte come lette
                                    </button>
                                </div>
                                <div className="max-h-96 overflow-y-auto">
                                    {notifications.length === 0 && (
                                        <div className="p-6 text-center text-sm" style={{ color: "var(--muted)" }}>Nessuna notifica</div>
                                    )}
                                    {notifications.map((n) => (
                                        <div key={n.id}
                                            data-testid={`notif-${n.id}`}
                                            className="p-3 hover:bg-stone-50 cursor-pointer"
                                            style={{ borderBottom: "1px solid var(--line)" }}>
                                            <div className="flex gap-2.5 items-start">
                                                {!n.read && <div className="w-2 h-2 rounded-full mt-1.5" style={{ background: "var(--magenta)" }}></div>}
                                                <div className={n.read ? "opacity-60 ml-4" : ""}>
                                                    <div className="font-semibold text-[13px]" style={{ color: "var(--navy)" }}>{n.title}</div>
                                                    {n.body && <div className="text-[12px] mt-0.5" style={{ color: "var(--text-2)" }}>{n.body}</div>}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        <button className="w-9 h-9 rounded-lg grid place-items-center bg-white transition-colors hover:border-pink-300"
                            style={{ border: "1px solid var(--line)", color: "var(--navy)" }} data-testid="topbar-search">
                            <Search size={16} />
                        </button>

                        <button
                            data-testid="topbar-user-menu"
                            onClick={() => { setOpenUser((v) => !v); setOpenBell(false); }}
                            className="flex items-center gap-2 ml-2 bg-white rounded-lg px-2.5 py-1.5 transition-colors hover:border-pink-300"
                            style={{ border: "1px solid var(--line)" }}
                        >
                            <div className="w-7 h-7 rounded-md grid place-items-center text-white font-bold text-[11px]"
                                style={{ background: "var(--navy)" }}>
                                {(user?.name || "??").split(" ").map(w => w[0]).slice(0,2).join("").toUpperCase()}
                            </div>
                            <span className="text-[13px] font-semibold hidden md:block" style={{ color: "var(--navy)" }}>{user?.name}</span>
                            <ChevronDown size={14} style={{ color: "var(--muted)" }} />
                        </button>
                        {openUser && (
                            <div className="absolute right-0 top-12 w-56 card-tpl shadow-xl overflow-hidden" style={{ borderColor: "var(--line-2)" }}>
                                <div className="p-3" style={{ borderBottom: "1px solid var(--line)" }}>
                                    <div className="font-bold text-sm" style={{ color: "var(--navy)" }}>{user?.name}</div>
                                    <div className="text-[11px] mono uppercase tracking-wider mt-1" style={{ color: "var(--muted)" }}>{user?.role}</div>
                                    <div className="text-[11px] truncate mt-1" style={{ color: "var(--text-2)" }}>{user?.email}</div>
                                </div>
                                <button
                                    data-testid="logout-btn"
                                    onClick={handleLogout}
                                    className="w-full flex items-center gap-2 px-3 py-2.5 text-[13px] font-semibold hover:bg-stone-50"
                                    style={{ color: "var(--red)" }}
                                >
                                    <LogOut size={14} /> Esci
                                </button>
                            </div>
                        )}
                    </div>
                </header>

                <main className="px-8 py-6 flex-1">
                    <Outlet />
                </main>
            </div>
        </ModuleAccent>
    );
}
